// Выбор человека из реестра — один на все разделы.
//
// Заведён 26.08.2026, когда выбор получателя понадобился мастеру заявки в разделе «Деньги», а
// реестр людей живёт в разделе «Люди». Класть его вторым запросом в чужой модуль нельзя: два
// ключа кэша на один и тот же список означают два запроса за одним ответом и две раскладки
// одних и тех же людей на соседних экранах.
//
// Право здесь то же, что и у реестра, — `core.employee.view`, и оно есть у старшего объекта:
// именно он подаёт заявку и указывает, кому отдал форму или билет. Область видимости при этом
// режет сервер выборкой: старший видит людей своих объектов, а не всю компанию.
//
// Состояние вахты здесь НЕ отбирает людей, и умолчание «На вахте», которым открывается реестр,
// сюда не попадает: выбор ищет по ВСЕМ. Мастер «добавить человека в ведомость» обязан находить
// и уволенного в середине расчётного периода, иначе его последние дни останутся неоплаченными.

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { EmployeeOut } from '@/shared/api/endpoints/people';
import { listEmployees } from '@/shared/api/endpoints/people';
import type { ListResponse } from '@/shared/api/types';
import type { ApiError } from '@/shared/errors/api-error';
import { formatDateOnly } from '@/shared/time/format';

/** Сколько людей показывать в выборе. Больше полусотни в раскрывающемся списке не читают. */
const ПОТОЛОК = 50;

/**
 * Пометка у уволенного в подписи выбора: « · уволен 29.03.2026». Даты нет — пометки нет вовсе.
 *
 * Состояние «уволен» можно поставить и пачкой из реестра, а там дата не спрашивается, поэтому у
 * части уволенных `fired_at` пуст. Пометка «уволен —» с прочерком на месте даты читалась как
 * поломка (замечание проверки 01.10.2026), а голое «уволен» без даты ничего не добавляет к
 * тому, что такой человек и так стоит в конце списка.
 */
export function пометкаУволен(человек: Pick<EmployeeOut, 'status' | 'fired_at'>): string {
  if (человек.status !== 'fired' || человек.fired_at == null || человек.fired_at === '') {
    return '';
  }
  return ` · уволен ${formatDateOnly(человек.fired_at)}`;
}

/**
 * Уволенных вниз списка и с пометкой в подписи; порядок «по фамилии» внутри обеих групп прежний.
 *
 * Список НЕ режется: уволенного ищут, когда считают его последний месяц, и спрятать его значит
 * оставить без денег за отработанное. Но среди работающих он стоять не должен: в выборе
 * старшего или получателя денег он чаще ошибка, чем цель.
 *
 * Пометка добавляется к `display_name`, потому что именно его все выборы рисуют подписью строки:
 * правка здесь, в общем слое, а не в четырёх разделах, которыми владеют разные люди.
 */
export function уволенныеВниз(люди: readonly EmployeeOut[]): EmployeeOut[] {
  const работающие = люди.filter((человек) => человек.status !== 'fired');
  const уволенные = люди
    .filter((человек) => человек.status === 'fired')
    .map((человек) => ({ ...человек, display_name: `${человек.display_name}${пометкаУволен(человек)}` }));
  return [...работающие, ...уволенные];
}

/** Вынесено в функцию модуля: запрос пересчитывает `select` при каждой новой ссылке на него. */
function разложить(ответ: ListResponse<EmployeeOut>): ListResponse<EmployeeOut> {
  return { ...ответ, items: уволенныеВниз(ответ.items) };
}

/**
 * Люди для выбора: по фамилии, уволенные стоят в конце, с датой увольнения, если она известна.
 *
 * `q` пустой — первые пятьдесят по фамилии; этого хватает старшему объекта, у которого людей
 * десяток. Финотделу с двумя сотнями нужен поиск, и поле для него стоит рядом с выбором.
 */
export function useЛюдиДляВыбора(q?: string): UseQueryResult<ListResponse<EmployeeOut>, ApiError> {
  const params = { q: q === undefined || q === '' ? undefined : q, limit: ПОТОЛОК };
  return useQuery<ListResponse<EmployeeOut>, ApiError>({
    queryKey: ['people', 'lookup', params],
    queryFn: ({ signal }) => listEmployees(params, signal),
    select: разложить,
    // Список не пропадает, пока едет следующий: человек печатает фамилию по букве, и мигание
    // выбора под курсором читается как поломка.
    placeholderData: keepPreviousData,
  });
}
